// Public read of the authoritative market stats snapshot.
// No auth required: row-level RLS allows everyone to read.
//
// CANONICAL SNAPSHOT RULE (P-23). All UI surfaces and all Orion reasoning
// that consumes market state MUST derive `delta`, `dailyChangePct`,
// session-derived, and volatility-derived fields from `MarketStats.snapshot`
// produced here. No render-time recomputation is permitted.
// `conventionVersion` is shipped on every read so consumers can tag
// derivations to a stable interpretation.
//
// CONVENTION CUTOVER (P-24 Phase D). The canonical `snapshot` block is now
// produced under the `v2-gold-institutional-close` convention (17:00 New
// York, DST-aware), which is the reference every institutional terminal uses
// for the gold daily change. `snapshotV2` is retained as an alias of the same
// canonical values so existing validation and parity tooling keeps compiling.
// The v1 UTC-midnight derivation remains available as `snapshotV1` for
// replay, parity reports, and shadow analytics only. No UI consumes it.

import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export type MarketSnapshotConventionVersion =
  | "v1-utc-midnight"
  | "v2-gold-institutional-close";

/**
 * Canonical market snapshot. One server-produced object, consumed verbatim by
 * every UI surface. Derivation discipline is enforced here, not at the edge.
 *
 * Price authority (read-side, UI canonical):
 *   - `livePrice`        = freshest spot tick from `gold_price_cache`.
 *   - `snapshotPrice`    = price stamped onto `market_stats_snapshot` at the
 *                          last cron tick (~14m floor).
 *   - `effectivePrice`   = `livePrice ?? snapshotPrice`. This is the
 *                          read-time canonical price for ALL UI derivations.
 *
 * Replay note. ODE / lineage / replay paths read the snapshot row directly
 * via the admin client. They continue to use the cron-frozen
 * `daily_change_pct` stored on the row, which is replay-deterministic. The
 * `snapshot.dailyChangePct` returned here is the read-time UI canonical
 * value (computed against `effectivePrice`) and is intentionally NOT used by
 * runtime reasoning. The two values converge each cron tick.
 */
export type CanonicalMarketSnapshot = {
  // Identity
  conventionVersion: MarketSnapshotConventionVersion;
  fetchedAt: string | null;
  dayKey: string | null;
  weekKey: string | null;
  priceSource: string | null;

  // Prices
  snapshotPrice: number | null;
  livePrice: number | null;
  livePriceFetchedAt: string | null;
  effectivePrice: number | null;

  // Daily derivation (read-time, anchored on previousClose)
  previousClose: number | null;
  dailyDelta: number | null;
  dailyChangePct: number | null;

  // Freshness
  priceAgeMs: number | null;
  isFresh: boolean; // priceAgeMs <= 90s
  isStale: boolean; // priceAgeMs >  5min
};

export type MarketStats = {
  price: number | null;
  priceSource: string | null;
  fetchedAt: string | null;
  dayKey: string | null;
  daily: {
    open: number | null;
    high: number | null;
    low: number | null;
    range: number | null;
    midpoint: number | null;
    changePct: number | null;
  };
  prevDay: { high: number | null; low: number | null; close: number | null };
  asia: { open: number | null; high: number | null; low: number | null; range: number | null };
  london: { open: number | null; high: number | null; low: number | null; range: number | null };
  ny: { open: number | null; high: number | null; low: number | null; range: number | null };
  currentSession: "asia" | "london" | "ny" | "off" | null;
  sessionPositionPct: number | null;
  weekly: {
    open: number | null;
    high: number | null;
    low: number | null;
    close: number | null;
    changePct: number | null;
  };
  avgDailyRange5d: number | null;
  avgAbsDailyChangePct20d: number | null;
  /**
   * Canonical snapshot block under the ACTIVE runtime convention
   * (`v2-gold-institutional-close`). Consumers read this exclusively.
   */
  snapshot: CanonicalMarketSnapshot;
  /**
   * Alias of `snapshot`, retained for validation and parity tooling.
   */
  snapshotV2: CanonicalMarketSnapshot;
  /**
   * Legacy UTC-midnight derivation. Replay, parity reports, and shadow
   * analytics only. NOT for production UI consumption. See P-24.
   */
  snapshotV1: CanonicalMarketSnapshot;
};

const EMPTY_SNAPSHOT_V1: CanonicalMarketSnapshot = {
  conventionVersion: "v1-utc-midnight",
  fetchedAt: null,
  dayKey: null,
  weekKey: null,
  priceSource: null,
  snapshotPrice: null,
  livePrice: null,
  livePriceFetchedAt: null,
  effectivePrice: null,
  previousClose: null,
  dailyDelta: null,
  dailyChangePct: null,
  priceAgeMs: null,
  isFresh: false,
  isStale: false,
};

const EMPTY_SNAPSHOT_V2: CanonicalMarketSnapshot = {
  ...EMPTY_SNAPSHOT_V1,
  conventionVersion: "v2-gold-institutional-close",
};

const EMPTY: MarketStats = {
  price: null,
  priceSource: null,
  fetchedAt: null,
  dayKey: null,
  daily: { open: null, high: null, low: null, range: null, midpoint: null, changePct: null },
  prevDay: { high: null, low: null, close: null },
  asia: { open: null, high: null, low: null, range: null },
  london: { open: null, high: null, low: null, range: null },
  ny: { open: null, high: null, low: null, range: null },
  currentSession: null,
  sessionPositionPct: null,
  weekly: { open: null, high: null, low: null, close: null, changePct: null },
  avgDailyRange5d: null,
  avgAbsDailyChangePct20d: null,
  snapshot: EMPTY_SNAPSHOT_V2,
  snapshotV2: EMPTY_SNAPSHOT_V2,
  snapshotV1: EMPTY_SNAPSHOT_V1,
};

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const FRESH_MS = 90_000;
const STALE_MS = 5 * 60_000;

export const getMarketStats = createServerFn({ method: "GET" }).handler(async () => {
  const { publicReader } = await import("@/lib/public-reader.server");
  const reader = publicReader() ?? supabaseAdmin;
  const [snapRes, liveRes] = await Promise.all([
    reader.from("market_stats_snapshot").select("*").eq("id", 1).maybeSingle(),
    reader.from("gold_price_cache").select("price, fetched_at").eq("id", 1).maybeSingle(),
  ]);

  if (snapRes.error) {
    console.error("[getMarketStats] read failed", snapRes.error.message);
    return EMPTY;
  }
  if (!snapRes.data) return EMPTY;

  const r = snapRes.data as Record<string, unknown>;
  const snapshotPrice = num(r.price);
  const previousClose = num(r.prev_day_close);
  const previousCloseV2 = num(r.prev_day_close_v2);
  const dailyOpen = num(r.daily_open);
  let livePrice = num(liveRes.data?.price);
  let livePriceFetchedAt = (liveRes.data?.fetched_at as string | null) ?? null;
  // Stale cache (> 5 min): fall back to the newest OTAS M5 close (read-only).
  const cacheAtMs = livePriceFetchedAt ? Date.parse(livePriceFetchedAt) : null;
  if (cacheAtMs == null || Date.now() - cacheAtMs > STALE_MS) {
    const { data: bar } = await supabaseAdmin
      .from("otas_m5_bars")
      .select("t, c")
      .order("t", { ascending: false })
      .limit(1)
      .maybeSingle();
    const barAtMs = bar?.t != null ? Math.min((Number(bar.t) + 300) * 1000, Date.now()) : null;
    if (bar?.c != null && barAtMs != null && (cacheAtMs == null || barAtMs > cacheAtMs)) {
      livePrice = num(bar.c);
      livePriceFetchedAt = new Date(barAtMs).toISOString();
    }
  }

  // Effective UI-canonical price: freshest tick wins.
  const effectivePrice = livePrice ?? snapshotPrice;

  // ---- v1 (UTC-midnight) read-time derivation
  const dailyReferenceV1 = previousClose ?? dailyOpen;
  const dailyDeltaV1 =
    effectivePrice != null && dailyReferenceV1 != null
      ? effectivePrice - dailyReferenceV1
      : null;
  const dailyChangePctV1 =
    dailyDeltaV1 != null && dailyReferenceV1 != null && dailyReferenceV1 !== 0
      ? (dailyDeltaV1 / dailyReferenceV1) * 100
      : null;

  // ---- v2 (institutional close) read-time derivation
  const dailyDeltaV2 =
    effectivePrice != null && previousCloseV2 != null
      ? effectivePrice - previousCloseV2
      : null;
  const dailyChangePctV2 =
    dailyDeltaV2 != null && previousCloseV2 != null && previousCloseV2 !== 0
      ? (dailyDeltaV2 / previousCloseV2) * 100
      : null;

  // Price age = newest of the two timestamped sources.
  const snapFetchedAtMs = r.fetched_at ? Date.parse(String(r.fetched_at)) : NaN;
  const liveFetchedAtMs = livePriceFetchedAt ? Date.parse(livePriceFetchedAt) : NaN;
  const newestPriceTs = [snapFetchedAtMs, liveFetchedAtMs].filter((t) => Number.isFinite(t)) as number[];
  const priceAgeMs = newestPriceTs.length > 0 ? Math.max(0, Date.now() - Math.max(...newestPriceTs)) : null;

  const baseSnapshotFields = {
    fetchedAt: (r.fetched_at as string | null) ?? null,
    weekKey: (r.week_key as string | null) ?? null,
    priceSource: (r.price_source as string | null) ?? null,
    snapshotPrice,
    livePrice,
    livePriceFetchedAt,
    effectivePrice,
    priceAgeMs,
    isFresh: priceAgeMs != null && priceAgeMs <= FRESH_MS,
    isStale: priceAgeMs != null && priceAgeMs > STALE_MS,
  };

  const snapshotV1: CanonicalMarketSnapshot = {
    ...baseSnapshotFields,
    conventionVersion: "v1-utc-midnight",
    dayKey: (r.day_key as string | null) ?? null,
    previousClose,
    dailyDelta: dailyDeltaV1,
    dailyChangePct: dailyChangePctV1,
  };

  // Canonical: institutional 17:00 New York close. Falls back to the v1
  // derivation only when the institutional columns are not yet populated.
  const snapshot: CanonicalMarketSnapshot =
    previousCloseV2 != null
      ? {
          ...baseSnapshotFields,
          conventionVersion: "v2-gold-institutional-close",
          dayKey: (r.institutional_day_key as string | null) ?? (r.day_key as string | null) ?? null,
          previousClose: previousCloseV2,
          dailyDelta: dailyDeltaV2,
          dailyChangePct: dailyChangePctV2,
        }
      : snapshotV1;

  return {
    // Legacy `price` keeps reading the snapshot row's stored value for
    // backward compatibility. New consumers should prefer `snapshot.effectivePrice`.
    price: snapshotPrice,
    priceSource: snapshot.priceSource,
    fetchedAt: snapshot.fetchedAt,
    dayKey: snapshot.dayKey,
    daily: {
      open: num(r.daily_open_v2) ?? dailyOpen,
      high: num(r.daily_high_v2) ?? num(r.daily_high),
      low: num(r.daily_low_v2) ?? num(r.daily_low),
      range: num(r.daily_range_v2) ?? num(r.daily_range),
      midpoint: num(r.daily_midpoint),
      // Legacy `daily.changePct` mirrors the canonical read-time value so
      // any unmigrated consumer stays consistent with the Bias Card, the
      // Island, and the trading room header.
      changePct: snapshot.dailyChangePct ?? num(r.daily_change_pct),
    },
    prevDay: {
      high: num(r.prev_day_high),
      low: num(r.prev_day_low),
      close: snapshot.previousClose,
    },
    asia: {
      open: num(r.asia_open),
      high: num(r.asia_high),
      low: num(r.asia_low),
      range: num(r.asia_range),
    },
    london: {
      open: num(r.london_open),
      high: num(r.london_high),
      low: num(r.london_low),
      range: num(r.london_range),
    },
    ny: {
      open: num(r.ny_open),
      high: num(r.ny_high),
      low: num(r.ny_low),
      range: num(r.ny_range),
    },
    currentSession: (r.current_session as MarketStats["currentSession"]) ?? null,
    sessionPositionPct: num(r.session_position_pct),
    weekly: {
      open: num(r.weekly_open),
      high: num(r.weekly_high),
      low: num(r.weekly_low),
      close: num(r.weekly_close),
      changePct: num(r.weekly_change_pct),
    },
    avgDailyRange5d: num(r.avg_daily_range_5d),
    avgAbsDailyChangePct20d: num(r.avg_abs_daily_change_pct_20d),
    snapshot,
    snapshotV2: snapshot,
    snapshotV1,
  };
});
